import { useState } from 'react'
import { Alert, AlertTitle, Box, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material'
import { Lock } from '@mui/icons-material'
import type { NdaCurrentResponse, SaveNdaResponse } from '@api/types/nda'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { isSameNdaText, shortHash } from './ndaText'

interface NdaEditorProps {
  /** The version in force. `published: false` means the app is currently ungated. */
  current: NdaCurrentResponse
  title: string
  body: string
  onTitleChange: (value: string) => void
  onBodyChange: (value: string) => void
  /** Opens the confirmation — publishing is never one click. */
  onRequestPublish: () => void
  /** Throws the draft away and reloads the published text. */
  onDiscard: () => void
  isSaving: boolean
  /** How many people have accepted the version in force; they are the ones being sent back. */
  acceptedCurrentCount: number
  /** The result of the last save, so the panel can report which of the two things happened. */
  lastResult: SaveNdaResponse | null
  serverError: string | null
}

export function NdaEditor({
  current,
  title,
  body,
  onTitleChange,
  onBodyChange,
  onRequestPublish,
  onDiscard,
  isSaving,
  acceptedCurrentCount,
  lastResult,
  serverError,
}: NdaEditorProps) {
  const published = current.published
  const currentVersion = current.version ?? 0
  const nextVersion = currentVersion + 1

  const unchanged =
    published && isSameNdaText({ title, body }, { title: current.title ?? '', body: current.body ?? '' })
  const empty = body.trim().length === 0
  const [confirmDiscard, setConfirmDiscard] = useState(false)

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6">
          {published ? `Draft of version ${nextVersion}` : 'Draft of version 1'}
        </Typography>
        {published && (
          <Chip
            size="small"
            icon={<Lock sx={{ fontSize: 16 }} />}
            label={`Version ${currentVersion} is sealed`}
            variant="outlined"
          />
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {published ? (
          <>
            You are not editing version {currentVersion}. Published versions can never be changed —
            an acceptance has to point at the exact words that were agreed to. This is a draft of the{' '}
            <strong>next</strong> version, started from the text of version {currentVersion} (
            <Box component="code" sx={{ fontFamily: 'monospace' }}>
              {shortHash(current.contentHash)}
            </Box>
            ).
          </>
        ) : (
          <>
            Nothing has ever been published, so there is no agreement to accept and the app is
            currently open to everybody. Saving text here publishes version 1 and turns the gate on.
          </>
        )}
      </Typography>

      <TextField
        label="Title"
        value={title}
        onChange={(e) => onTitleChange(e.target.value)}
        fullWidth
        disabled={isSaving}
        sx={{ mb: 2 }}
        helperText="Shown as the heading of the acceptance screen. Counts as part of the text: changing it alone publishes a version."
      />

      <TextField
        label="Agreement"
        value={body}
        onChange={(e) => onBodyChange(e.target.value)}
        fullWidth
        multiline
        minRows={16}
        disabled={isSaving}
        error={empty}
        helperText={empty ? 'The agreement cannot be empty.' : ' '}
        inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.875rem', lineHeight: 1.6 } }}
      />

      {/* The consequence, stated before the button rather than after it. */}
      {!empty &&
        (unchanged ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>Nothing to publish</AlertTitle>
            This is character-for-character version {currentVersion} once line endings and
            surrounding blank space are ignored, so it hashes the same. Saving it would not create a
            version and would not ask anybody to accept anything again.
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <AlertTitle>
              Saving publishes version {published ? nextVersion : 1} and locks everyone out until
              they accept it
            </AlertTitle>
            {published ? (
              <>
                Every user is stopped at the acceptance screen the moment version {nextVersion}{' '}
                exists — they cannot use the app at all until they accept it, including the{' '}
                <strong>
                  {acceptedCurrentCount} {acceptedCurrentCount === 1 ? 'person' : 'people'}
                </strong>{' '}
                who already accepted version {currentVersion}. Version {currentVersion} and its
                signatures stay on the record untouched.
              </>
            ) : (
              <>
                The app is ungated right now. Publishing version 1 stops <strong>every user</strong>{' '}
                at the acceptance screen until they accept it.
              </>
            )}
          </Alert>
        ))}

      {serverError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {serverError}
        </Alert>
      )}

      {/* What actually happened. The server is the authority on whether a version was minted —
          it can differ from the prediction above if somebody else published in the meantime. */}
      {lastResult && (
        <Alert severity={lastResult.newVersion ? 'success' : 'info'} sx={{ mt: 2 }}>
          <AlertTitle>
            {lastResult.newVersion
              ? `Version ${lastResult.version} published`
              : 'Nothing changed'}
          </AlertTitle>
          {lastResult.message}
        </Alert>
      )}

      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 3 }}>
        <Button
          variant="contained"
          color="warning"
          onClick={onRequestPublish}
          disabled={isSaving || empty || unchanged}
        >
          {isSaving ? 'Publishing…' : `Publish version ${published ? nextVersion : 1}`}
        </Button>
        <Button variant="outlined" onClick={() => setConfirmDiscard(true)} disabled={isSaving || unchanged}>
          Discard draft
        </Button>
      </Stack>

      {/* The draft lives only in this page's state: once thrown away there is nothing to get back. */}
      <ConfirmDialog
        open={confirmDiscard}
        title="Discard this draft?"
        message={
          published
            ? `Your edits are thrown away and the editor goes back to the text of version ${currentVersion}. Drafts are not saved anywhere, so this cannot be undone.`
            : 'Your edits are thrown away and the editor is cleared. Drafts are not saved anywhere, so this cannot be undone.'
        }
        confirmLabel="Discard draft"
        destructive
        onConfirm={() => {
          setConfirmDiscard(false)
          onDiscard()
        }}
        onClose={() => setConfirmDiscard(false)}
      />
    </Paper>
  )
}
